/**
 * Re-review: deciding whether what the author just pushed actually clears the block.
 *
 * ⭐ THIS IS HALF THE PRODUCT, NOT A FOLLOW-UP. Across 107 reviews by this team no block is ever
 * left standing — all eight of the lead reviewer's change-request reviews resolve to approved or
 * dismissed within the same cycle. Blocks are raised freely *because* they are cheap to clear. A
 * reviewer that blocks and then cannot tell you whether you have fixed it is worse than one that
 * never blocked, and the failure mode this exists to prevent is a STALE RED MARK, not a red mark.
 *
 * ⛔ "THE AUTHOR TOUCHED THAT FILE" IS NOT RESOLUTION. A finding is resolved only when the specific
 * mechanism it named is gone — the same standard as the original finding, applied in reverse. The
 * check cites the new line the way the finding cited the old one, so a person can check the
 * reviewer's work.
 *
 * ⛔ NEVER RE-RAISE A FINDING A HUMAN DISMISSED. That is what the fingerprint is for, and it is why
 * the fingerprint is code-shaped rather than line-numbered: the author's push moves every line
 * number in the file.
 *
 * ⭐ SUPPRESS NEW NITS. Once a pull request has been reviewed, later passes report blocking findings
 * only. A one-line fix reaching round seven on style is how a reviewer gets muted.
 *
 * See docs/ARCHITECTURE.md, "Re-review and unblocking".
 */

import { normaliseCode, normaliseDimensionId } from "../triage/store.js"
import type { Finding, Severity, TriageState } from "../types/finding.js"

/** ⛔ Exactly three outcomes. docs/ARCHITECTURE.md fixes this list; it is not open for extension. */
export type ReReviewOutcome = "resolved" | "not_resolved" | "changed_shape"

/** A prior finding together with whatever a person decided about it. */
export interface PriorFinding {
  readonly finding: Finding
  readonly state: TriageState
}

/**
 * The tree the new commits produced. Injected, so this module reads no disk and no network — the
 * caller has already prepared the clone at the new head.
 */
export interface NewTree {
  readonly headSha: string
  /**
   * Paths the new commits touched.
   *
   * ⛔ This bounds the SEARCH. It never decides anything: a file appearing here is not evidence
   * that anything in it was fixed, and that conflation is the single most common way a re-review
   * lifts a block that still holds.
   */
  readonly changedPaths: readonly string[]
  /** Post-image lines of a path at `headSha`, or undefined when the file no longer exists. */
  readonly readFile: (path: string) => readonly string[] | undefined
}

export interface Citation {
  readonly path: string
  readonly line: number
  /** The line, byte-for-byte, so the citation can be checked rather than believed. */
  readonly text: string
}

export interface ReReviewDecision {
  readonly fingerprint: string
  readonly outcome: ReReviewOutcome
  /** Plain language. The subject is the capability, never an identifier. */
  readonly reason: string
  /** Where the mechanism lives now — present on every `not_resolved`, and that is the point. */
  readonly citation?: Citation
  /** For `changed_shape`: the fingerprint of the new finding standing in the old one's place. */
  readonly replacedBy?: string
}

export interface ReReviewInput {
  /** The prior BLOCKING findings. Filtering to blockers is the caller's job. */
  readonly prior: readonly PriorFinding[]
  /** What this pass found. Suppression is applied here, not by the caller. */
  readonly newFindings: readonly Finding[]
  readonly tree: NewTree
  /**
   * Severities below this are dropped on a re-review. Default `medium`, which suppresses new LOW
   * findings. docs/ARCHITECTURE.md argues the stricter reading — blocking findings only — and that
   * is `"high"` here; it is a parameter rather than a constant so the choice is visible.
   */
  readonly suppressBelow?: Severity
}

export interface ReReviewResult {
  readonly decisions: readonly ReReviewDecision[]
  /** The subset that still blocks. Empty means the status flips. */
  readonly stillBlocking: readonly ReReviewDecision[]
  /** New findings that survived suppression. */
  readonly newFindings: readonly Finding[]
  /** ⛔ Fingerprints a human dismissed, which were not looked at and will not be raised again. */
  readonly suppressedDismissed: readonly string[]
  /** Fingerprints of new nits dropped because this is a re-review. */
  readonly suppressedLowSeverity: readonly string[]
  /** ⛔ The status IS the mechanism. Every blocker cleared ⇒ `success`. */
  readonly commitStatus: "success" | "failure"
}

const SEVERITY_RANK: Record<Severity, number> = { high: 3, medium: 2, low: 1 }

/**
 * A quote shorter than this is matched only as a whole normalised line.
 *
 * ⚠️ Without a floor, a finding quoting `}` would be "still present" in every file in the
 * repository and no block could ever lift.
 */
export const MIN_CONTAINMENT_CHARS = 8

/**
 * Is the mechanism the finding named still in the tree, and where?
 *
 * ⭐ The cited file is searched first, then every file the new commits touched — because code that
 * was MOVED was not removed, and a re-review that lifts a block on a cut-and-paste is exactly the
 * kind of wrong that destroys trust in the loop.
 *
 * ⚠️ Whole-line equality first, then containment. Containment is deliberately generous: the safe
 * error here is refusing to lift a block, never lifting one that still holds.
 */
export function findMechanism(
  tree: NewTree,
  quote: string,
  citedPath: string,
): Citation | undefined {
  const needle = normaliseCode(quote)
  if (needle === "") return undefined
  const paths = [citedPath, ...tree.changedPaths.filter((p) => p !== citedPath)]
  const allowContainment = needle.length >= MIN_CONTAINMENT_CHARS

  for (const path of paths) {
    const lines = tree.readFile(path)
    if (lines === undefined) continue
    for (let i = 0; i < lines.length; i++) {
      const raw = lines[i]
      if (raw === undefined) continue
      const norm = normaliseCode(raw)
      if (norm === needle || (allowContainment && norm.includes(needle))) {
        return { path, line: i + 1, text: raw }
      }
    }
  }
  return undefined
}

function sameRule(a: Finding, b: Finding): boolean {
  return normaliseDimensionId(a.dimensionId) === normaliseDimensionId(b.dimensionId)
}

/**
 * Decide, per prior blocking finding, whether the new commits cleared it.
 *
 * Nothing here reads a file or a socket; `tree.readFile` is the only door to the world.
 */
export function reReview(input: ReReviewInput): ReReviewResult {
  const floor = SEVERITY_RANK[input.suppressBelow ?? "medium"]
  const suppressedLowSeverity: string[] = []
  const survivingNew: Finding[] = []
  for (const f of input.newFindings) {
    if (SEVERITY_RANK[f.severity] < floor) suppressedLowSeverity.push(f.fingerprint)
    else survivingNew.push(f)
  }

  const suppressedDismissed: string[] = []
  const decisions: ReReviewDecision[] = []

  for (const p of input.prior) {
    // ⛔ A human already answered this. It is not re-raised, not re-decided, and not counted as a
    // blocker — whatever the code now says.
    if (p.state.kind === "dismissed") {
      suppressedDismissed.push(p.finding.fingerprint)
      continue
    }

    const citedPath = p.finding.anchor.path
    const hit = findMechanism(input.tree, p.finding.quote, citedPath)

    if (hit !== undefined) {
      // ⛔ THE CENTRAL RULE. The file may be unrecognisable and the line number may have moved by
      // two hundred; the mechanism is still reachable, so the question the block asked is still
      // unanswered. The citation is what lets the author argue with it.
      const moved = hit.path !== citedPath || hit.line !== p.finding.anchor.line
      decisions.push({
        fingerprint: p.finding.fingerprint,
        outcome: "not_resolved",
        reason: moved
          ? `the code this raised moved to ${hit.path}:${hit.line} but still does the same thing, so the question it asked is still open`
          : `the code this raised is unchanged at ${hit.path}:${hit.line}`,
        citation: hit,
      })
      continue
    }

    const touched = input.tree.changedPaths.includes(citedPath)
    if (!touched) {
      // Defensive floor. The mechanism vanished from a file nobody edited, which means the tree we
      // were handed does not describe the tree we reviewed. Refusing to lift is the safe error.
      decisions.push({
        fingerprint: p.finding.fingerprint,
        outcome: "not_resolved",
        reason: `${citedPath} is not among the files these commits changed, so nothing here could have cleared this`,
      })
      continue
    }

    const successor = survivingNew.find(
      (n) => n.anchor.path === citedPath && sameRule(n, p.finding),
    )
    if (successor !== undefined) {
      // The original is gone and a new defect stands in its place: the old comment resolves and the
      // new one is a NEW finding, with its own fingerprint and its own triage decision to make.
      decisions.push({
        fingerprint: p.finding.fingerprint,
        outcome: "changed_shape",
        reason: `the original is gone, and a new problem of the same kind now stands at ${successor.anchor.path}:${successor.anchor.line}`,
        citation: {
          path: successor.anchor.path,
          line: successor.anchor.line,
          text: successor.quote,
        },
        replacedBy: successor.fingerprint,
      })
      continue
    }

    decisions.push({
      fingerprint: p.finding.fingerprint,
      outcome: "resolved",
      reason: `${citedPath} changed and the code this raised is no longer anywhere in the files these commits touched`,
    })
  }

  const stillBlocking = decisions.filter((d) => d.outcome === "not_resolved")
  const newBlockers = survivingNew.filter((f) => f.severity === "high" && f.cls === "introduced")
  const commitStatus: "success" | "failure" =
    stillBlocking.length === 0 && newBlockers.length === 0 ? "success" : "failure"

  return {
    decisions,
    stillBlocking,
    newFindings: survivingNew,
    suppressedDismissed,
    suppressedLowSeverity,
    commitStatus,
  }
}

/**
 * The reply a decision posts on its own thread.
 *
 * ⭐ Say the resolution rule, not just the verdict. A block whose exit condition is unstated turns a
 * review into a negotiation, and the corpus states it every time.
 */
export function replyBody(d: ReReviewDecision): string {
  switch (d.outcome) {
    case "resolved":
      return `Cleared by the latest commits: ${d.reason}.`
    case "changed_shape":
      return `This one is cleared — ${d.reason}. The replacement is raised separately and needs its own decision.`
    case "not_resolved": {
      const where =
        d.citation === undefined
          ? ""
          : `\n\n\`${d.citation.path}:${d.citation.line}\`\n\n\`\`\`\n${d.citation.text}\n\`\`\``
      return `Still holding: ${d.reason}.${where}`
    }
    default: {
      // ⛔ A runtime floor under an exhaustive switch. An untrusted value falling through here
      // returns undefined and surfaces as a TypeError in a file that names something unrelated.
      const impossible: never = d.outcome
      throw new Error(`unknown re-review outcome: ${String(impossible)}`)
    }
  }
}
